// Cửa sổ vật phẩm: tooltip 5 khối (UI_SPEC §11, ITEMS §10), cửa vật phẩm xếp chồng, bộ lọc tự
// phân giải (ITEMS §8.4). Không cuộn: ruột ≤ 5 khối, khối ④ rút gọn khi dài.
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { hasKey, t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { relativeTime } from '@i18n/time';
import { baseDef } from '@data/items/bases';
import { glyphDef } from '@data/items/glyphs';
import { uniqueDef } from '@data/items/uniques';
import { RARITIES, type ItemInstance, type Rarity } from '@data/items/types';
import { parseStackKey, goodsDef } from '@data/items/goods';
import { ledger } from '@state/modules/ledger';
import { playerBase } from '@sim/world/ctx';
import {
  compareToEquipped,
  isPerfect,
  itemPower,
  mainStats,
  rollPercent,
  type Loadout,
} from '@sim/items/stats';
import { enhanceChance, enhanceCost, isProtected, protectReason, salvageYield } from '@sim/items/nurture';
import { pillBlock } from '@sim/crafting';
import { cultivation } from '@state/modules/cultivation';
import {
  FILTER_MAX_RARITY,
  consumePill,
  enhance,
  enhanceTransferQuote,
  equip,
  findItem,
  inventory,
  markSeen,
  salvage,
  salvageByFilter,
  salvageNeedsConfirm,
  setFilter,
  toggleLock,
  transferEnhance,
  unequip,
  unequipBlock,
} from '@state/modules/inventory';
import { wallet } from '@state/modules/wallet';
import { askConfirm, closeWindows } from '@state/modules/ui';
import { now } from '@state/clock';
import { ItemSlot } from '../../kit/ItemSlot';
import { TextButton } from '../../kit/TextButton';
import { Icon } from '../../icons';
import {
  affixName,
  elementClass,
  formatAffix,
  goodsArt,
  goodsIcon,
  goodsName,
  goodsTier,
  itemArt,
  itemIcon,
  itemName,
  rarityClass,
} from '../../itemView';

const fmt = (n: number): string => formatFull(Math.round(n));

function delta(n: number): JSX.Element | null {
  if (Math.abs(n) < 0.5) return <i class="cmp" />;
  return <i class={n > 0 ? 'cmp up' : 'cmp down'}>{n > 0 ? `▲ ${fmt(n)}` : `▼ ${fmt(-n)}`}</i>;
}

function formerOwnerKey(uniqueId: string): string {
  const u = uniqueDef(uniqueId);
  for (const v of u.formerOwnerVariants ?? []) {
    const [flag, val] = v.when.split('=') as [string, string];
    if (String(ledger.value.flags[flag]) === val) return `${uniqueId}.formerOwner.${v.suffix}`;
  }
  return `${uniqueId}.formerOwner`;
}

/** Dải nguồn gốc ④: boss "Rơi từ …", nơi chốn "Nhặt ở …"; nguồn lạ (món cũ đã làm sạch) → "nơi không rõ". */
function originText(source: string, gotAtMs: number): string {
  const key = hasKey(`${source}.name`) ? `${source}.name` : 'unknown.name';
  return t(source.startsWith('boss.') && key !== 'unknown.name' ? 'ui.item.originBoss' : 'ui.item.origin', {
    src: t(key),
    when: relativeTime(gotAtMs, now()),
  });
}

/** Khóa i18n tên món làm tham số (`@…`) cho dải hỏi. */
function nameParam(it: ItemInstance): string {
  return `@${it.uniqueId ?? it.base}.name`;
}

/** Phân giải tay: Thiên+ và món được bảo vệ phải qua dải hỏi (UI_SPEC §4.3). */
function askSalvage(it: ItemInstance): void {
  const y = salvageYield(it);
  const done = (): void => {
    if (salvage(it.uid)) closeWindows();
  };
  if (!salvageNeedsConfirm(it)) return done();
  const why = protectReason(it);
  askConfirm({
    textKey: why ? 'ui.item.salvageAskProtected' : 'ui.item.salvageAsk',
    textParams: { item: nameParam(it), dust: y.dust, ...(why ? { why: `@ui.item.protect.${why}` } : {}) },
    confirmKey: 'ui.item.salvage',
    onConfirm: done,
  });
}

/** Mặc; món bị thay có cấp cường hóa chuyển được thì mời chuyển cấp (ITEMS §8.1) bằng dải hỏi. */
function doEquip(uid: string): void {
  const r = equip(uid);
  if (!r) return;
  closeWindows();
  const q = r.replaced ? enhanceTransferQuote(r.replaced.uid, uid) : null;
  // Chỉ mời khi đủ Linh Thạch (dải hỏi không có nút câm).
  if (!q || !r.replaced || wallet.peek().spiritStone < q.spiritStone) return;
  const to = findItem(uid)!.item;
  askConfirm({
    textKey: 'ui.item.transferAsk',
    textParams: { item: nameParam(to), n: q.level, st: fmt(q.spiritStone) },
    confirmKey: 'ui.item.transfer',
    onConfirm: () => void transferEnhance(r.replaced!.uid, uid),
  });
}

/** Tooltip 5 khối: ① head · ② chỉ số · ③ chân ngôn · ④ lore · ⑤ hành động. */
export function ItemDetailWindow({ uid }: { uid: string }): JSX.Element {
  const inv = inventory.value;
  const found = findItem(uid);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => markSeen(uid), [uid]);
  useEffect(
    () => () => {
      if (flashTimer.current !== null) clearTimeout(flashTimer.current);
    },
    [],
  );
  if (!found) return <p class="ht-block">{t('ui.item.gone')}</p>;
  const it = found.item;
  const b = baseDef(it.base);
  const base = playerBase(cultivation.value);
  const cmp = found.slot ? null : compareToEquipped(base, inv.equipped as Loadout, it);
  const other = cmp ? inv.equipped[cmp.slot] : null;
  const mains = mainStats(it);
  const otherMains = other ? mainStats(other) : [];
  const q = rarityClass(it.rarity);
  const cost = enhanceCost(it);
  const w = wallet.value;
  const canEnh = !!cost && w.refiningStone >= cost.refiningStone && w.spiritStone >= cost.spiritStone;
  const y = salvageYield(it);
  const glyph = it.daoGlyph ? glyphDef(it.daoGlyph.id) : null;
  const unequipBlocked = !!found.slot && unequipBlock() !== null;
  const typeLine = [
    t(`item.rarity.${it.rarity}`),
    t(b.weaponType ? `item.weapon.${b.weaponType}` : `item.slot.${b.slot}`),
    t('ui.item.giai', { n: it.giai }),
    it.enhance > 0 ? `+${it.enhance}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div class="item-detail" data-id="item-detail">
      <div class="id-head">
        <ItemSlot
          size="xl"
          q={q}
          art={itemArt(it)}
          glyph={itemIcon(it)}
          el={elementClass(b.element)}
          br={it.enhance ? `+${it.enhance}` : undefined}
        />
        <div>
          <b class={`id-name ${q}`} data-id="item-name">
            {itemName(it)}
          </b>
          <small>{typeLine}</small>
          <small class="id-lc">
            {t('ui.item.power', { n: fmt(itemPower(base, it)) })} {cmp && delta(cmp.delta)}
          </small>
        </div>
      </div>

      <div class="id-stats">
        {mains.map((m) => {
          const o = otherMains.find((x) => x.key === m.key);
          return (
            <div class="id-line main" key={m.key}>
              <span>{t(`ui.item.main.${m.key}`)}</span>
              <b>{fmt(m.value)}</b>
              {cmp ? delta(m.value - (o?.value ?? 0)) : <i class="cmp" />}
            </div>
          );
        })}
        {b.implicit.map((imp) => (
          <div class="id-line main" key={imp.affix}>
            <span>{affixName(imp.affix)}</span>
            <b>{formatAffix(imp.affix, imp.value, it.giai)}</b>
            <i class="cmp" />
          </div>
        ))}
        {it.lines.map((l, i) => (
          <div
            class={isPerfect(l) ? 'id-line sub perfect' : 'id-line sub'}
            key={`${l.affix}-${i}`}
            data-id="item-line"
          >
            <span>
              {affixName(l.affix)} {formatAffix(l.affix, l.value, it.giai)}
            </span>
            <small>
              {isPerfect(l)
                ? t('ui.item.perfect')
                : t('ui.item.tier', { t: l.tier, p: Math.round(rollPercent(l) * 100) })}
            </small>
            <i class="cmp" />
          </div>
        ))}
        {glyph && (
          <div class="id-line glyph">
            <span>{t('ui.item.glyph', { name: t(`glyph.${glyph.id}.name`), n: it.daoGlyph!.level })}</span>
          </div>
        )}
      </div>

      <p class="id-mantra">
        {it.uniqueId ? (
          <>
            <b>
              {t('ui.item.onEquip')} {t(`${it.uniqueId}.effect.name`)}
            </b>{' '}
            · {t(`${it.uniqueId}.effect.desc`)}
          </>
        ) : glyph ? (
          <>
            <b>
              {t('ui.item.onEquip')} {t(`glyph.${glyph.id}.name`)}
            </b>{' '}
            · {t(`glyph.${glyph.id}.desc`, { v: formatFull(glyph.byLevel[it.daoGlyph!.level - 1]!) })}
          </>
        ) : (
          <>
            <b>{t('ui.item.onEquip')}</b> {t(`${it.base}.trait`)}
          </>
        )}
      </p>

      <div class="id-lore">
        <i>{t(it.uniqueId ? `${it.uniqueId}.lore` : `${it.base}.lore`)}</i>
        {it.uniqueId && (
          <small class="id-owner">{t('ui.item.formerOwner', { text: t(formerOwnerKey(it.uniqueId)) })}</small>
        )}
        <small class="id-origin">{originText(it.source, it.gotAtMs)}</small>
      </div>

      <div class="id-actions">
        <div class="ht-actions">
          {found.slot ? (
            <TextButton
              variant="ghost"
              id="item-unequip"
              disabled={unequipBlocked}
              onClick={() => unequip(found.slot!) && closeWindows()}
            >
              {t('ui.item.unequip')}
            </TextButton>
          ) : (
            <TextButton id="item-equip" onClick={() => doEquip(uid)}>
              {t('ui.item.equip')}
            </TextButton>
          )}
          <TextButton
            variant={found.slot ? 'primary' : 'ghost'}
            id="item-enhance"
            disabled={!canEnh}
            onClick={() => {
              const r = enhance(uid);
              setFlash(r);
              if (flashTimer.current !== null) clearTimeout(flashTimer.current);
              flashTimer.current = setTimeout(() => setFlash(null), 620);
            }}
          >
            {flash === 'success'
              ? t('ui.item.enhanceOk')
              : flash === 'fail'
                ? t('ui.item.enhanceFail')
                : t('ui.item.enhance')}
          </TextButton>
          {!found.slot && (
            <TextButton
              variant="ghost"
              id="item-salvage"
              disabled={!!it.locked}
              onClick={() => askSalvage(it)}
            >
              {t('ui.item.salvage')}
            </TextButton>
          )}
          <TextButton variant="ghost" id="item-lock" onClick={() => toggleLock(uid)}>
            {it.locked ? t('ui.item.unlock') : t('ui.item.lock')}
          </TextButton>
        </div>
        <p
          class={(canEnh || !cost) && !unequipBlocked ? 'ht-reason dim' : 'ht-reason'}
          data-id="item-enhance-cost"
        >
          {unequipBlocked && `${t('ui.item.block.bagFull')} `}
          {cost
            ? t('ui.item.enhanceCost', {
                n: it.enhance + 1,
                rs: cost.refiningStone,
                have: w.refiningStone,
                st: fmt(cost.spiritStone),
                p: Math.round(enhanceChance(it) * 100),
              })
            : t('ui.item.enhanceMax')}
          {!found.slot && ` · ${t('ui.item.salvageYield', { dust: y.dust })}`}
        </p>
      </div>
    </div>
  );
}

/** Cửa vật phẩm xếp chồng (đan, linh dược, nguyên liệu). */
export function GoodsWindow({ stackKey }: { stackKey: string }): JSX.Element {
  const n = inventory.value.goods[stackKey] ?? 0;
  const { id } = parseStackKey(stackKey);
  const kind = goodsDef(id).kind;
  const tox = cultivation.value.pillToxicity;
  const block = kind === 'pill' ? pillBlock(stackKey, tox) : null;
  return (
    <>
      <div class="id-head">
        <ItemSlot
          size="xl"
          q={rarityClass(goodsTier(stackKey))}
          art={goodsArt(stackKey)}
          glyph={goodsIcon(stackKey)}
          br={n > 1 ? fmt(n) : undefined}
        />
        <div>
          <b class={`id-name ${rarityClass(goodsTier(stackKey))}`}>{goodsName(stackKey)}</b>
          <small>{t(`ui.goods.kind.${kind}`)}</small>
          <small>{t('ui.goods.have', { n: fmt(n) })}</small>
        </div>
      </div>
      <p class="ht-block">{t(`${id}.desc`)}</p>
      {kind === 'pill' && (
        <div class="ht-actions">
          <TextButton id="goods-use" disabled={block !== null || n < 1} onClick={() => consumePill(stackKey)}>
            {t('ui.goods.use')}
          </TextButton>
          <span class="ht-chip">{t('ui.goods.toxicity', { n: Math.round(tox) })}</span>
        </div>
      )}
      {block && <p class="ht-reason">{t(`ui.goods.block.${block}`)}</p>}
    </>
  );
}

const FILTER_RARITIES: Rarity[] = RARITIES.slice(0, RARITIES.indexOf(FILTER_MAX_RARITY) + 1);

/** Bộ lọc tự phân giải (ITEMS §8.4): phẩm ≤ X · không dòng ≥ T2 · Giai thấp hơn ≥ N. */
export function FilterWindow(): JSX.Element {
  const f = inventory.value.filter;
  return (
    <>
      <p class="ht-block">{t('ui.filter.body')}</p>
      <div class="ht-rows">
        <div class="ht-statline">
          <span>{t('ui.filter.enabled')}</span>
          <TextButton
            id="filter-toggle"
            variant={f.enabled ? 'primary' : 'ghost'}
            pressed={f.enabled}
            onClick={() => setFilter({ ...f, enabled: !f.enabled })}
          >
            {f.enabled ? t('ui.on') : t('ui.off')}
          </TextButton>
        </div>
        <div class="ht-statline">
          <span>{t('ui.filter.rarity')}</span>
          <span class="filter-chips">
            {FILTER_RARITIES.map((r) => (
              <button
                type="button"
                key={r}
                class={`ht-chip q-chip ${rarityClass(r)}${f.maxRarity === r ? ' sel' : ''}`}
                aria-pressed={f.maxRarity === r}
                data-id={`filter-rarity-${r}`}
                onClick={() => setFilter({ ...f, maxRarity: r })}
              >
                {t(`item.rarity.${r}`)}
              </button>
            ))}
          </span>
        </div>
        <div class="ht-statline">
          <span>{t('ui.filter.tier')}</span>
          <TextButton
            variant={f.requireNoHighTier ? 'primary' : 'ghost'}
            pressed={f.requireNoHighTier}
            onClick={() => setFilter({ ...f, requireNoHighTier: !f.requireNoHighTier })}
          >
            {f.requireNoHighTier ? t('ui.on') : t('ui.off')}
          </TextButton>
        </div>
        <div class="ht-statline">
          <span>{t('ui.filter.giai', { n: f.giaiGap ?? 0 })}</span>
          <TextButton
            variant={f.giaiGap !== null ? 'primary' : 'ghost'}
            pressed={f.giaiGap !== null}
            onClick={() =>
              setFilter({ ...f, giaiGap: f.giaiGap === null ? 2 : f.giaiGap >= 6 ? null : f.giaiGap + 2 })
            }
          >
            {f.giaiGap === null ? t('ui.off') : t('ui.filter.giaiN', { n: f.giaiGap })}
          </TextButton>
        </div>
      </div>
      <div class="ht-actions">
        <TextButton id="filter-apply" variant="ghost" onClick={() => salvageByFilter()}>
          {t('ui.filter.applyNow')}
        </TextButton>
        <span class="ht-chip price wallet" style={{ '--cur-rgb': 'var(--cur-dust-rgb)' }}>
          <Icon name="dust" />
          {fmt(wallet.value.dust)}
        </span>
      </div>
      <p class="ht-reason dim">{t('ui.filter.never')}</p>
    </>
  );
}

export { RARITIES, isProtected };
